import { forwardRef, InputHTMLAttributes, useRef } from 'react';

import {
  checkboxInvalidStyle,
  checkboxStyle,
  checkedStateEnabledStyle,
  checkedStateStyle,
  labelWrapperEnabledStyle,
  labelWrapperStyle,
} from './Checkbox.css';
import { useInputIds } from '../../hooks/useInputIds';
import { useValidationErrorEvent } from '../../hooks/useValidationErrorEvent';
import { CheckIcon } from '../../icons';
import { useI18nTranslation } from '../../providers/i18n';
import { TextProps } from '../../types';
import { mergeRefs } from '../../util';
import { Grid } from '../Grid/Grid';
import { InputBaseProps } from '../Input/Input';
import { InputError } from '../Input/InputError';
import { Hint } from '../Input/InputHint';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

type InputCheckboxHTMLAttributes = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'onFocus' | 'defaultChecked'
>;

export interface Props extends InputBaseProps, Omit<InputCheckboxHTMLAttributes, 'isReadOnly'> {
  isChecked?: boolean;
  weight?: TextProps['weight'];
}

export const Checkbox = forwardRef<HTMLInputElement, Props>(
  ({ error, hint, isChecked, isDisabled = false, isOptional, label, name, weight, ...restProps }, ref) => {
    const { inputId, describedBy, errorId, hintId } = useInputIds({ error, hint });
    const i18nOptionalText = useI18nTranslation('optional');

    const localRef = useRef<HTMLDivElement>(null);
    // Merge the local ref and forwarded ref so we can forward it *and* use it locally
    const mergedRef = mergeRefs([ref, localRef]);

    useValidationErrorEvent({ error, name }, localRef);

    const inputClassName = error ? `${checkboxStyle} ${checkboxInvalidStyle}` : checkboxStyle;
    const checkedStateClassName = isDisabled ? checkedStateStyle : `${checkedStateStyle} ${checkedStateEnabledStyle}`;
    const labelWrapperClassName = isDisabled ? labelWrapperStyle : `${labelWrapperStyle} ${labelWrapperEnabledStyle}`;

    return (
      <Stack gap="2">
        <label>
          <Grid gridTemplateColumns="min-content auto" gridTemplateRows="1fr">
            <input
              aria-describedby={describedBy}
              aria-invalid={error ? true : undefined}
              aria-required={!isOptional}
              checked={isChecked}
              data-label={label}
              disabled={isDisabled}
              id={inputId}
              className={inputClassName}
              name={name}
              ref={mergedRef}
              type="checkbox"
              {...restProps}
            />
            <div className={checkedStateClassName}>
              <CheckIcon color={isDisabled ? 'iconSecondary' : 'iconInverted'} size="small" />
            </div>
            <span className={labelWrapperClassName}>
              <Text size="BodyM" color="textPrimary" weight={weight}>
                {label}
                {isOptional ? <span>{` (${i18nOptionalText})`}</span> : null}
              </Text>
            </span>
          </Grid>
        </label>

        {error ? (
          <InputError id={errorId} hasArrow={false}>
            {error}
          </InputError>
        ) : null}
        {hint ? <Hint id={hintId}>{hint}</Hint> : null}
      </Stack>
    );
  },
);

Checkbox.displayName = 'Checkbox';
